import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React from 'react';
import { formatRange } from './customCloseout';
import { closeoutTestId } from './test-ids';

type CancelCustomPeriodModalProps = {
  open: boolean;
  onClose: () => void;
  alertData: any;
  onConfirm: () => void;
  confirmLoading?: boolean;
  confirmError?: string | null;
};

/**
 * BOPS-115 — cancel (delete) a custom period. Offered only before its first close & sign, so
 * nothing is locked yet and the removal fully unwinds.
 */
const CancelCustomPeriodModal: React.FC<CancelCustomPeriodModalProps> = ({
  open,
  onClose,
  alertData,
  onConfirm,
  confirmLoading = false,
  confirmError = null,
}) => (
  <Dialog
    data-testid={closeoutTestId('cancel-period-dialog')}
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { borderRadius: 2 } }}
  >
    <DialogTitle sx={{ fontSize: '1.25rem', fontWeight: 600, pb: 0 }}>
      Cancel closeout period?
    </DialogTitle>
    <DialogContent>
      <Typography
        variant="body2"
        sx={{ color: '#6C737F', pt: 2 }}
        component="div"
      >
        {alertData?.range
          ? `The closeout period ${formatRange(
              alertData.range.startDate,
              alertData.range.endDate
            )} will be removed.`
          : 'This closeout period will be removed.'}
        <br />
        <br />
        Nothing has been locked for this period yet, so cancelling it changes no documentation or
        payer data. You can create a new period for these dates afterwards.
      </Typography>
      {confirmError ? (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
        >
          {confirmError}
        </Alert>
      ) : null}
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
      <Button
        color="inherit"
        onClick={onClose}
        disabled={confirmLoading}
        data-testid={closeoutTestId('cancel-period-dismiss')}
        sx={{ color: 'text.secondary' }}
      >
        Keep period
      </Button>
      <Button
        variant="contained"
        color="error"
        onClick={onConfirm}
        disabled={confirmLoading}
        data-testid={closeoutTestId('cancel-period-confirm')}
        sx={{ textTransform: 'none' }}
      >
        {confirmLoading ? 'Cancelling…' : 'Cancel period'}
      </Button>
    </DialogActions>
  </Dialog>
);

export default CancelCustomPeriodModal;
